import React, { useMemo } from 'react';

import {
  TextField,
  Datagrid,
  ArrayField,
  usePermissions,
  isEmpty,
} from 'react-admin';

import {
  Box,
  Card,
  CardHeader,
  Divider,
} from '@mui/material';

import { boldDataGridStyle } from '../../../../../constants/style/datagridStyles';

import useRelatedEntities from '../../../../../hooks/useRelatedEntities';
import NoResults from '../../../../layout/NoResults';
import entityTypes from '../../../../../constants/entityTypes';
import StatusField from '../../../../../customFields/StatusField';
import ColoredScoreField from '../../../../../customFields/ColoredScoreField';
import ReferenceEntityField from '../../../../../customFields/ReferenceEntityField';
import LinkField from '../../../../layout/LinkField';
import { NationalIdentifierSingleField } from '../../../../../customFields/NationalIdentifier';
import actions, { Actions } from '../../../../../constants/actions';
import hasAccess from '../../../../../utilities/hasAccess';
import PermissionMissing from '../../../../utils/PermissionMissing';

const RelatedIndividualsList = () => {
  const { entities } = useRelatedEntities();

  const individuals = useMemo(() => entities
    .filter((individual) => individual.entityType === entityTypes.INDIVIDUAL), [entities]);

  const { permissions } = usePermissions<Actions[]>();

  if (!hasAccess(permissions, actions.INDIVIDUAL_LIST)) {
    return (
      <Card variant="outlined">
        <Box padding={4} display="flex" alignItems="center" width="100%" flexDirection="column">
          <PermissionMissing />
        </Box>
      </Card>
    );
  }

  if (isEmpty(individuals)) {
    return (
      <Card variant="outlined">
        <Box padding={4} display="flex" alignItems="center" width="100%" flexDirection="column">
          <NoResults />
        </Box>
      </Card>
    );
  }

  return (
    <Box width="100%">
      <Card variant="outlined">
        <CardHeader
          sx={{
            color: 'black',
            fontWeight: 'bold',
          }}
          slotProps={{ title: { variant: 'inherit' } }}
          title={`Related individuals: ${individuals.length}`}
        />
        <Divider />
        <ArrayField record={{ individuals }} source="individuals">
          <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} resource="individuals">
            <LinkField source="entityId" sortable={false} />
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="First name" linkType={false}>
              <TextField source="firstName" />
            </ReferenceEntityField>
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="Last name" linkType={false}>
              <TextField source="lastName" />
            </ReferenceEntityField>
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="National identifier" linkType={false}>
              <NationalIdentifierSingleField underline={false} />
            </ReferenceEntityField>
            <TextField source="relation" sortable={false} />
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="Risk score" linkType={false}>
              <ColoredScoreField source="riskScore" />
            </ReferenceEntityField>
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="Population register collected" linkType={false}>
              <StatusField source="populationRegisterCollected" />
            </ReferenceEntityField>
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="Potential PEP" linkType={false}>
              <StatusField source="isPep" />
            </ReferenceEntityField>
            <ReferenceEntityField idKey="entityId" typeKey="entityType" source="" label="Potentially sanctioned" linkType={false}>
              <StatusField source="isSanctioned" />
            </ReferenceEntityField>
          </Datagrid>
        </ArrayField>
      </Card>
    </Box>
  );
};

export default RelatedIndividualsList;
